import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

interface Props {
  children: ReactNode
  /** Optional: kompakte Variante (z.B. innerhalb des Layouts statt Vollbild) */
  inline?: boolean
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Globale Fehlergrenze. Faengt Render-Fehler einer Seite ab, statt die ganze
 * SPA in eine weisse Seite zu reissen (siehe Bug #71: ein fehlender Import
 * -> "X is not defined" -> ohne Boundary war die komplette App leer).
 *
 * Bewusst dependency-arm (nur Tailwind + ein Icon), damit die Boundary auch
 * dann rendert, wenn gemeinsame UI-Komponenten selbst defekt sind.
 *
 * Reset: Die Boundary wird im Layout mit key={pathname} gemountet -> beim
 * Navigieren auf eine andere Seite remountet sie frisch und der Fehler ist weg.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Sichtbar in der Browser-Konsole fuer Fehlerberichte
    console.error('[ErrorBoundary] Render-Fehler abgefangen:', error, info.componentStack)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const message = this.state.error?.message || 'Unbekannter Fehler'

    return (
      <div
        className={
          this.props.inline
            ? 'flex min-h-[50vh] items-center justify-center p-6'
            : 'flex min-h-screen items-center justify-center bg-gray-50 p-6'
        }
      >
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-6 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h1 className="mb-1 text-lg font-semibold text-gray-900">
            Diese Seite konnte nicht geladen werden
          </h1>
          <p className="mb-4 text-sm text-gray-500">
            Ein unerwarteter Fehler ist aufgetreten. Die uebrige Anwendung
            funktioniert weiter — du kannst links eine andere Seite oeffnen.
          </p>

          <div className="mb-4 rounded-md bg-gray-50 p-3 text-left">
            <p className="break-words font-mono text-xs text-gray-600">{message}</p>
          </div>

          <div className="flex justify-center gap-2">
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Seite neu laden
            </button>
            <button
              onClick={() => {
                window.location.href = '/admin'
              }}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Zur Startseite
            </button>
          </div>

          <p className="mt-4 text-xs text-gray-400">
            Bitte melde diesen Fehlertext im Bug-Tracker, falls er bestehen bleibt.
          </p>
        </div>
      </div>
    )
  }
}

export default ErrorBoundary
